import React, { useEffect, useMemo, useRef, useState } from 'react'
import PageHeader from 'components/ds/PageHeader'
import DataTable from 'components/ds/DataTable'
import { Input, Select } from 'components/ds/Input'
import { Switch } from 'components/ds/Choice'
import { Tag } from 'components/ds/Badge'
import Button from 'components/ds/Button'
import EmptyState from 'components/ds/EmptyState'
import { getEmailTemplates, previewEmailTemplate } from 'actions/applicants'
import styles from './EmailTemplates.module.scss'

const ALL = 'ALL'

// Force the template's dark-mode rules on or off, whatever the viewer's system setting is.
const withColorScheme = (html, dark) =>
    html.replace(/\(prefers-color-scheme:\s*dark\)/g, dark ? '(min-width: 0px)' : '(max-width: 0px)')

/**
 * Superadmin catalog of every email the platform sends. The backend renders each one from its real template with
 * sample data, so the preview is what recipients get.
 */
const EmailTemplates = () => {
    const [templates, setTemplates] = useState(null)
    const [loadFailed, setLoadFailed] = useState(false)
    const [search, setSearch] = useState('')
    const [audience, setAudience] = useState(ALL)
    const [selectedKey, setSelectedKey] = useState(null)
    const [variantKey, setVariantKey] = useState(null)
    const [preview, setPreview] = useState(null)
    const [previewFailed, setPreviewFailed] = useState(false)
    const [dark, setDark] = useState(false)
    const [frameHeight, setFrameHeight] = useState(640)
    const frameRef = useRef(null)

    const load = () => {
        setLoadFailed(false)
        getEmailTemplates()
            .then(res => {
                const list = Array.isArray(res) ? res : []
                setTemplates(list)
                if (list.length) select(list[0])
            })
            .catch(() => setLoadFailed(true))
    }

    useEffect(load, []) // eslint-disable-line react-hooks/exhaustive-deps

    const select = template => {
        setSelectedKey(template.key)
        setVariantKey(template.variants[0]?.key || null)
    }

    // A late answer for a previous selection must not replace the current preview.
    useEffect(() => {
        if (!selectedKey) return
        let current = true
        setPreviewFailed(false)
        // Don't show the previous email's subject and body under the newly selected one while it loads.
        setPreview(null)
        previewEmailTemplate(selectedKey, variantKey)
            .then(res => { if (current) setPreview(res) })
            .catch(() => { if (current) { setPreview(null); setPreviewFailed(true) } })
        return () => { current = false }
    }, [selectedKey, variantKey])

    const audiences = useMemo(() => [...new Set((templates || []).map(t => t.audience))], [templates])

    const rows = useMemo(() => {
        const q = search.trim().toLowerCase()
        return (templates || []).filter(t =>
            (audience === ALL || t.audience === audience) &&
            (!q || [t.name, t.template, t.sentBy, t.description].some(v => (v || '').toLowerCase().includes(q))))
    }, [templates, search, audience])

    const selected = (templates || []).find(t => t.key === selectedKey)
    const variant = selected?.variants.find(v => v.key === variantKey)

    const measureFrame = () => {
        const height = frameRef.current?.contentDocument?.documentElement?.scrollHeight
        if (height) setFrameHeight(height)
    }

    const columns = [
        {
            key: 'name',
            header: 'Email',
            render: t => (
                <>
                    <span className={styles.name}>{t.name}</span>
                    <span className={styles.template}>{t.template}</span>
                </>
            ),
        },
        { key: 'audience', header: 'Sent to', width: 150, render: t => <Tag>{t.audience}</Tag> },
    ]

    if (loadFailed) {
        return (
            <div className={styles.page}>
                <EmptyState
                    kind="error"
                    title="Email templates couldn't load"
                    action={<Button variant="secondary" onClick={load}>Try again</Button>}
                />
            </div>
        )
    }

    return (
        <div className={styles.page}>
            <PageHeader
                title="Email Templates"
                lede="Every email the platform sends, rendered from its real template with sample data."
                facts={templates && <span className={styles.caption}>{templates.length} emails</span>}
            />

            <div className={styles.columns}>
                <section className={styles.listPane} aria-label="Emails">
                    <div className={styles.filters}>
                        <Input
                            label="Search"
                            placeholder="Name, template or sender"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                        <Select label="Sent to" value={audience} onChange={e => setAudience(e.target.value)}>
                            <option value={ALL}>Everyone</option>
                            {audiences.map(a => <option key={a} value={a}>{a}</option>)}
                        </Select>
                    </div>
                    <DataTable
                        caption="Emails the platform sends"
                        columns={columns}
                        rows={rows}
                        getRowId={t => t.key}
                        onRowClick={select}
                        selectedIds={selectedKey ? [selectedKey] : []}
                        emptyState={templates ? 'No emails match these filters.' : 'Loading…'}
                    />
                </section>

                <section className={styles.previewPane} aria-label="Preview">
                    {selected && (
                        <>
                            <div className={styles.previewHead}>
                                <h2 className={styles.title}>{selected.name}</h2>
                                <p className={styles.description}>{selected.description}</p>
                                <dl className={styles.facts}>
                                    <dt>Sent to</dt><dd>{selected.audience}</dd>
                                    <dt>Template</dt><dd className={styles.mono}>{selected.template}</dd>
                                    <dt>Sent by</dt><dd className={styles.mono}>{selected.sentBy}</dd>
                                </dl>
                                <div className={styles.controls}>
                                    {selected.variants.length > 1 && (
                                        <Select label="Version" value={variantKey || ''} onChange={e => setVariantKey(e.target.value)}>
                                            {selected.variants.map(v => <option key={v.key} value={v.key}>{v.label}</option>)}
                                        </Select>
                                    )}
                                    <Switch label="Dark" checked={dark} onChange={e => setDark(e.target.checked)} />
                                </div>
                            </div>
                            <div className={styles.envelope}>
                                <span className={styles.caption}>Subject</span>
                                <span className={styles.subject}>{preview?.subject || variant?.subject}</span>
                            </div>
                            {previewFailed ? (
                                <div className={styles.previewError}>This email couldn't be rendered. Try another version or reload the page.</div>
                            ) : (
                                <iframe
                                    ref={frameRef}
                                    title={`${selected.name} preview`}
                                    className={styles.frame}
                                    style={{ height: frameHeight }}
                                    srcDoc={withColorScheme(preview?.html || '', dark)}
                                    sandbox="allow-same-origin"
                                    onLoad={measureFrame}
                                />
                            )}
                        </>
                    )}
                </section>
            </div>
        </div>
    )
}

export default EmailTemplates
